import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { LoadingIcon } from '@/components/icons';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useTreePrompt from '@/data/hook/useTreePrompt';
import { promptInstructionsQueryKeys } from '@/data/services/querykeys';
import PromptInstruction, {
  PromptCategoryEnum,
} from '@/models/PromptInstruction';
import useFilterParams from '@/utils/UseFilterParams';
import {
  FolderIcon,
  FolderOpenIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { PromptsFiltersParams } from '../../PromptsPageContent';
import PromptsTreeList from '../PromptsTreeList';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';

type PromptsTreeItemProps = {
  prompt: PromptInstruction;
};

export default function PromptsTreeItem({ prompt }: PromptsTreeItemProps) {
  const tree = useTreePrompt();

  useEffect(
    () => tree.insert({ prompt, parentElementId: prompt.parentPromptId || 0 }),
    [tree, prompt],
  );

  const hasChildren = !prompt.parentPromptId;
  const { pathParams } = useTreePrompt();
  const [isOpen, setIsOpen] = useState(pathParams.includes(prompt.id));

  const {
    results: childPrompts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
    cacheManager,
  } = useInfiniteService({
    ...promptInstructionsQueryKeys.list({
      parentPromptId: [prompt.id],
      category: Object.values(PromptCategoryEnum),
    })._ctx.infinity,
    enabled: isOpen && hasChildren,
  });

  useEffect(() => {
    setIsOpen(open => {
      if (!open && pathParams.includes(prompt.id)) return true;
      return open;
    });
  }, [pathParams, prompt.id]);

  useEffect(() => {
    tree.cacheObserver.subscribe({
      id: prompt.id,
      action: (prompt?: PromptInstruction) => {
        if (!prompt) return;
        cacheManager.update(prompt);
      },
    });
    return () => tree.cacheObserver.unsubscribe(prompt.id);
  }, [prompt, tree.cacheObserver, cacheManager]);

  return (
    <li>
      <ConditionalRenderer condition={hasChildren}>
        <PromptParentButton
          prompt={prompt}
          onClick={() => setIsOpen(prev => !prev)}
          isOpen={isOpen}
          isLoading={isInitialLoading}
        />
        <ConditionalRenderer condition={isOpen}>
          <PromptsTreeList
            items={childPrompts}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </ConditionalRenderer>
      </ConditionalRenderer>
      <ConditionalRenderer condition={!hasChildren}>
        <PromptLeafButton prompt={prompt} />
      </ConditionalRenderer>
    </li>
  );
}

type GetIconArgs = {
  isLoading?: boolean;
  isOpen?: boolean;
  isLeaf?: boolean;
};

function getIcon({ isLoading, isOpen, isLeaf }: GetIconArgs) {
  if (isLoading) return LoadingIcon;
  if (isLeaf) return SparklesIcon;
  return isOpen ? FolderOpenIcon : FolderIcon;
}

type PromptParentButtonProps = {
  prompt: PromptInstruction;
  isOpen?: boolean;
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

function PromptParentButton({
  prompt,
  isOpen,
  onClick,
  isLoading,
}: PromptParentButtonProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts.categories',
  });

  const promptKey = promptInstructionsQueryKeys.get(prompt.id);

  const singleCacheManager = useSingleCache(promptKey.queryKey);

  const { setFilterParams, filterParams } =
    useFilterParams<PromptsFiltersParams>();
  const { getPath, pathParams } = useTreePrompt();
  const selectedId = Number(filterParams.get('prompt') ?? NaN);
  const isSelected = selectedId === prompt.id;

  const Icon = getIcon({ isOpen, isLoading });
  const onClickSetPrompt: MouseEventHandler<HTMLDivElement> = () => {
    singleCacheManager.set(prompt);
    const path = getPath(prompt.id);
    setFilterParams({
      prompt: prompt.id,
      path,
    });
  };

  const style = isSelected
    ? 'font-500 text-primary'
    : pathParams.includes(prompt.id)
    ? 'font-500'
    : 'font-400';

  return (
    <div className="flex gap-1.5 items-center">
      <IconButton
        icon={<Icon className="w-5 h-5 text-primary" />}
        onClick={onClick}
      />
      <span className="grow cursor-default truncate">
        <Text
          text={t(`${prompt.category}.name`)}
          className={twMerge('truncate cursor-pointer', style)}
          onClick={onClickSetPrompt}
        />
      </span>
    </div>
  );
}

type PromptLeafButtonProps = {
  prompt: PromptInstruction;
};

function PromptLeafButton({ prompt }: PromptLeafButtonProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts.categories',
  });

  const promptKey = promptInstructionsQueryKeys.get(prompt.id);

  const singleCacheManager = useSingleCache(promptKey.queryKey);

  const { setFilterParams, filterParams } =
    useFilterParams<PromptsFiltersParams>();
  const { getPath } = useTreePrompt();
  const selectedId = Number(filterParams.get('prompt') ?? NaN);
  const isSelected = selectedId === prompt.id;

  const onClickSetPrompt: MouseEventHandler<HTMLDivElement> = () => {
    singleCacheManager.set(prompt);
    const path = getPath(prompt.id);
    setFilterParams({
      prompt: prompt.id,
      path,
    });
  };

  const Icon = getIcon({ isLeaf: true });

  return (
    <div
      className="flex gap-1.5 items-center cursor-pointer hover:bg-base-200 rounded-lg p-1 transition-colors max-w-[calc(100%-1.5rem)]"
      onClick={onClickSetPrompt}
    >
      <Icon className="w-5 h-5 text-primary shrink-0" />
      <span className="grow truncate">
        <Text
          text={t(`${prompt.category}.name`)}
          className={twMerge(
            'truncate',
            isSelected ? 'font-500 text-primary' : 'font-400',
          )}
        />
      </span>
    </div>
  );
}
